import { MATTER_RELATED_YES_LABEL } from '@/constants/visualPrompts/matterStep';
import type {
  VisualPromptAnswers,
  VisualPromptAnswerValue,
  VisualPromptDefinition,
} from '@/types/visualPrompts/visualPrompt.types';

const formatAnswerValue = (value: VisualPromptAnswerValue | undefined): string => {
  if (value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map((item) => `- ${item}`).join('\n');
  }
  return String(value).trim();
};

const replacePlaceholders = (
  template: string,
  answers: VisualPromptAnswers,
): string => {
  let result = template;
  const keys = new Set<string>();
  const placeholderRegex = /\{\{(\w+)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = placeholderRegex.exec(template)) !== null) {
    keys.add(match[1]);
  }

  keys.forEach((key) => {
    const formatted = formatAnswerValue(answers[key]);
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), formatted);
  });

  return result;
};

const stripEmptySections = (text: string): string => {
  return text
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return true;
      }
      const colonIndex = trimmed.indexOf(':');
      if (colonIndex === -1) {
        return true;
      }
      const afterColon = trimmed.slice(colonIndex + 1).trim();
      return afterColon.length > 0;
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const applyTransformRules = (
  definition: VisualPromptDefinition,
  compiled: string,
  answers: VisualPromptAnswers,
): string => {
  const rules = definition.transformRules?.appendIfPresent;
  if (!rules) {
    return compiled;
  }
  const appendLines: string[] = [];
  Object.entries(rules).forEach(([key, line]) => {
    const value = answers[key];
    if (!isEmptyAnswer(value)) {
      appendLines.push(line);
    }
  });
  if (appendLines.length === 0) {
    return compiled;
  }
  return `${compiled}\n\n${appendLines.join('\n')}`;
};

const isEmptyAnswer = (value: VisualPromptAnswerValue | undefined): boolean => {
  if (value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return String(value).trim() === '';
};

const applyMatterGuidance = (
  compiled: string,
  answers: VisualPromptAnswers,
): string => {
  const matter = String(answers.matterRelated ?? '');
  if (matter !== MATTER_RELATED_YES_LABEL) {
    return compiled;
  }
  return `${compiled}

Matter-related guidance:
- Treat content as potentially confidential.
- Do not add client names, matter titles, or privileged detail unless already in the source material.
- Use neutral descriptions (e.g. "the matter" or "the client system") where possible.
- This is operational comms support, not legal advice.`;
};

export const compileVisualPrompt = (
  definition: VisualPromptDefinition,
  answers: VisualPromptAnswers,
): string => {
  const mergedAnswers: VisualPromptAnswers = {
    ...definition.defaults,
    ...answers,
  };

  if (mergedAnswers.sourceText && String(mergedAnswers.sourceText).trim()) {
    const text = String(mergedAnswers.sourceText).trim();
    mergedAnswers.sourceText = `Text:\n${text}`;
  }

  let compiled = replacePlaceholders(definition.compileTemplate, mergedAnswers);
  compiled = stripEmptySections(compiled);
  compiled = applyTransformRules(definition, compiled, mergedAnswers);
  compiled = applyMatterGuidance(compiled, mergedAnswers);
  return compiled.trim();
};

const PREVIEW_MIN_LENGTH = 80;

export const isCompiledPreviewThin = (
  compiled: string,
  requiresContent?: boolean,
): boolean => {
  if (!requiresContent) {
    return false;
  }
  const body = compiled.replace(/^Text:\s*/i, '').trim();
  return body.length < PREVIEW_MIN_LENGTH;
};
